import React, { useEffect, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, CircularProgress,
  ToggleButton, ToggleButtonGroup, Chip, IconButton, Snackbar, Alert, Divider,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PeopleIcon from "@mui/icons-material/People";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { getFinanceSummary, addFinanceEntry, deleteFinanceEntry } from "../api/adminApi";

const fmt = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN")}`;

const StatCardEarn = ({ icon, label, value, color }) => (
  <Paper className="glass-card" sx={{ p: 3.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "16px", bgcolor: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="#64748b" fontWeight={600} sx={{ letterSpacing: "0.3px" }}>{label}</Typography>
        <Typography fontWeight={800} fontSize={24} color="#1e293b">{value}</Typography>
      </Box>
    </Box>
  </Paper>
);

const Earnings = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("earning");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const load = async () => {
    try {
      setSummary(await getFinanceSummary());
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    const amt = Number(amount);
    if (!amount || !Number.isFinite(amt) || amt <= 0) {
      setSnack({ open: true, message: "Enter a valid amount", severity: "error" });
      return;
    }
    setSaving(true);
    try {
      await addFinanceEntry({ type, amount: amt, note: note.trim() });
      setAmount("");
      setNote("");
      await load();
      setSnack({ open: true, message: "Entry added", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteFinanceEntry(id);
      await load();
      setSnack({ open: true, message: "Entry deleted", severity: "success" });
    } catch (err) {
      setSnack({ open: true, message: err.message, severity: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleExport = () => {
    const rows = [
      ["Date", "Type", "Amount (Rs.)", "Note"],
      ...entries.map((e) => [
        new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
        e.type === "earning" ? "Earning" : "Investment",
        e.amount,
        e.note || "",
      ]),
      [],
      ["Total Investment", "", summary?.totalInvestment || 0],
      ["Total Earning", "", summary?.totalEarning || 0],
      ["Net Profit", "", summary?.net ?? 0],
    ];
    const csv = rows
      .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `earnings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  const net = summary?.net ?? 0;
  const entries = summary?.entries || [];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4 }}>
        <Box>
          <Typography fontWeight={800} fontSize={24} color="#1e293b">Earnings</Typography>
          <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>Investment & earning tracker</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={entries.length === 0}
          sx={{ textTransform: "none", fontWeight: 600, color: "#6366f1", borderColor: "#6366f166", "&:hover": { borderColor: "#6366f1", bgcolor: "#6366f108" } }}
        >
          Export CSV
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: "27px", mb: 5 }}>
        <StatCardEarn icon={<TrendingDownIcon sx={{ color: "#ef4444", fontSize: 24 }} />} label="Total Investment" value={fmt(summary?.totalInvestment)} color="#ef4444" />
        <StatCardEarn icon={<TrendingUpIcon sx={{ color: "#10b981", fontSize: 24 }} />} label="Total Earning" value={fmt(summary?.totalEarning)} color="#10b981" />
        <StatCardEarn icon={<AccountBalanceWalletIcon sx={{ color: net >= 0 ? "#10b981" : "#ef4444", fontSize: 24 }} />} label="Net Profit" value={fmt(net)} color={net >= 0 ? "#10b981" : "#ef4444"} />
        <StatCardEarn icon={<PeopleIcon sx={{ color: "#6366f1", fontSize: 24 }} />} label="Total Clients" value={Number(summary?.clientCount || 0).toLocaleString("en-IN")} color="#6366f1" />
      </Box>

      <Paper className="glass-card" sx={{ p: 3.5, mb: 5 }}>
        <Typography fontWeight={700} fontSize={15} color="#1e293b" sx={{ mb: 2.5 }}>Add Entry</Typography>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { md: "center" } }}>
          <ToggleButtonGroup
            exclusive
            value={type}
            onChange={(_, v) => v && setType(v)}
            size="small"
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, fontSize: 13, px: 2.5, py: 1, "&.Mui-selected": { bgcolor: type === "earning" ? "#10b981" : "#ef4444", color: "#fff", "&:hover": { bgcolor: type === "earning" ? "#059669" : "#dc2626" } } } }}
          >
            <ToggleButton value="earning"><TrendingUpIcon sx={{ fontSize: 18, mr: 0.5 }} />Earning</ToggleButton>
            <ToggleButton value="investment"><TrendingDownIcon sx={{ fontSize: 18, mr: 0.5 }} />Investment</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            label="Amount (₹)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ width: { xs: "100%", md: 200 }, "& .MuiOutlinedInput-root": { fontSize: 14 } }}
            InputProps={{ startAdornment: <Typography variant="body2" color="#64748b" sx={{ mr: 0.5 }}>₹</Typography> }}
          />
          <TextField
            size="small"
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ flex: 1, minWidth: 180, "& .MuiOutlinedInput-root": { fontSize: 14 } }}
            placeholder="e.g. Server cost, Client #3 payment"
          />
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAdd}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: type === "earning" ? "#10b981" : "#ef4444", "&:hover": { bgcolor: type === "earning" ? "#059669" : "#dc2626" }, px: 3, py: 1.1 }}
          >
            Add Entry
          </Button>
        </Box>
      </Paper>

      <Paper className="glass-card" sx={{ p: 3.5 }}>
        <Typography fontWeight={700} fontSize={15} color="#1e293b" sx={{ mb: 2.5 }}>
          History {entries.length > 0 && <Typography component="span" variant="caption" color="#94a3b8">({entries.length})</Typography>}
        </Typography>
        {entries.length === 0 ? (
          <Typography variant="body2" color="#94a3b8" sx={{ py: 4, textAlign: "center" }}>
            No entries yet. Add your first investment or earning above.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {entries.map((e, i) => (
              <React.Fragment key={e._id}>
                {i > 0 && <Divider sx={{ borderColor: "#f1f5f9" }} />}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: `${e.type === "earning" ? "#10b981" : "#ef4444"}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {e.type === "earning" ? <TrendingUpIcon sx={{ color: "#10b981", fontSize: 18 }} /> : <TrendingDownIcon sx={{ color: "#ef4444", fontSize: 18 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={e.type === "earning" ? "Earning" : "Investment"}
                        size="small"
                        sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: e.type === "earning" ? "#dcfce7" : "#fee2e2", color: e.type === "earning" ? "#059669" : "#dc2626" }}
                      />
                      <Typography variant="body2" fontWeight={700} color="#1e293b">{fmt(e.amount)}</Typography>
                    </Box>
                    {e.note && (
                      <Typography variant="caption" color="#64748b" sx={{ display: "block", mt: 0.3, wordBreak: "break-word" }}>{e.note}</Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="#94a3b8" sx={{ flexShrink: 0 }}>
                    {new Date(e.date).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" })}
                  </Typography>
                  <IconButton size="small" onClick={() => handleDelete(e._id)} disabled={deletingId === e._id} sx={{ color: "#94a3b8", "&:hover": { color: "#ef4444" }, flexShrink: 0 }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        )}
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity}
          sx={{ width: "100%", borderRadius: 2 }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Earnings;
